import type { AlgoliaSearchHelper } from "algoliasearch-helper";
import instantsearch from "instantsearch.js";
import { applyListingChipToUiState } from "./listing-chip-redirect";
import { initSearchQueryFilters } from "./search-query-filters";
import {
    searchBox,
    refinementList,
    stats,
    sortBy,
    currentRefinements,
} from "instantsearch.js/es/widgets";
import {
    connectSortBy,
    connectPagination,
} from "instantsearch.js/es/connectors";
import { SearchClientFactory } from "@/services/SearchClientFactory";
import {
    applyCuratedSearchFiltersForSSR,
    applyMakeChangeDependentResets,
    buildLandingPaginationUrl,
    landingDisplayPageToInstantSearchPage,
    resolveLandingDisplayPageFromUrl,
    normalizeMakeSelection,
    createAlgoliaRouting,
    createCuratedPageStateChangeHandler,
    createMarketplaceBladeRouting,
    createStateMapping,
    initInstantSearchBridge,
    initRangeFiltersForSSR,
    parseUrlToRouteState,
    registerInstantSearchForRangeFilters,
    registerCuratedPathname,
    setCuratedSearchConfig,
    setOnUserFilterInteraction,
    suppressNextRouteWrite,
    uiStateToMarketplaceUrl,
    type CuratedSearchFilters,
} from "@/services/algoliaRouting.blade";
import {
    initActionBarFacetDropdowns,
    replayPendingActionBarTap,
} from "./action-bar-facet-dropdowns";
import { trackMarketplaceBarExposure } from "./ab-marketplace-bar";
import {
    countActiveMarketplaceFilters,
    updateFilterToggleLabels,
} from "./count-active-filters";
import { hiddenRefinementsState } from "@/services/algoliaRouting.blade";
import {
    countHiddenRefinements,
    isListingFiltered,
    isListingFilteredOnLoad,
    isNarrowViewport,
    searchPlaceholderFor,
    syncListingFilteredState,
} from "./listing-filtered-state";
import { setResultsCount, syncListingTrackingContext } from "./listing-tracking";
import {
    initActionBarFacetClearButtons,
    updateActionBarFacetDropdownLabels,
    type FacetClearHelper,
} from "./update-action-bar-facet-labels";
import { initMobileScrollChrome } from "./mobile-scroll-chrome";
import { E3_SORT_REFINE_EVENT, initMarketplaceBarE3 } from "./marketplace-bar-e3";
import { MAKE_DEPENDENT_REFINEMENT_FACETS } from "./widgets/make-menu";
import { updateMarketplaceFilterBarStickyTop } from "@/header/header-mode";
import {
    formatNumber,
    createMakeMenuWidget,
    createModelRefinementWidget,
    createMenuStyleRefinementWidget,
    createSpecificationSearchWidget,
    syncSpecificationSearchUi,
    createColorRefinementWidget,
    FALLBACK_EXTERIOR_COLORS,
    createMaterialRefinementWidget,
    createPromotionToggleWidget,
    createCustomRangeWidget,
    createClearAllFiltersWidget,
    clearAllMarketplaceFilters,
    createButtonFacetWidget,
    createCuratedConfigureWidget,
    createRangeFiltersPillsWidget,
    vehicleCardTemplate,
    scheduleVehicleCardCarouselInit,
    createReconciledMarketplaceHitsWidget,
    marketplaceHitsReconciled,
    marketplaceHitsSsrRemoved,
} from "./widgets";
import {
    injectPromoCards,
    isPromoCard,
    markMarketplaceLcpHit,
    promoCardTemplate,
} from "./promo-cards";
import { createMarketplaceHeaderAutocompleteWidget } from "./marketplace-header-autocomplete";
import {
    countActiveMoreFacets,
    initSidebarMoreFacets,
    isFacetHiddenInMoreGroup,
    updateMoreFacetsActiveCount,
} from "./sidebar-more-facets";
import { initCompareFavorite } from "./widgets/compare-favorite";
import { initMarketplaceSaveSearchModal } from "./save-search-modal";
import { marketplaceSortItems, visibleSortOptions } from "./sort-options";
import {
    applyCuratedDisplayLimit,
    createCuratedCurrentRefinementsWidget,
    createCuratedFacetWidgets,
    curatedHitsPerPage,
    curatedNoResultsTemplate,
    initCuratedActionBarTeleport,
    initCuratedReverseColor,
    initCuratedScrollRestoration,
    initCuratedSearchTeleport,
    renderCuratedPagination,
} from "./curated-layout";

// Test A/B de la barre mobile : exposition branchée avant tout le montage (barre rendue par le
// serveur), pour qu'une erreur plus bas, propre à une variante, ne déséquilibre pas les expositions
trackMarketplaceBarExposure();

initCompareFavorite();

// ── Config & constants ───────────────────────────────────────
const config = window.__MARKETPLACE_CONFIG__;
if (
    !config?.search?.indexName ||
    (!document.querySelector(".marketplace-page") &&
        !document.querySelector(".marketplace-excerpt"))
) {
    throw new Error(
        "marketplace-search: missing config or marketplace mount point",
    );
}
const {
    search: searchConfig,
    bodyTypeLabels,
    gearboxTypeLabels,
    referenceExteriorColors,
    referenceInteriorColors,
} = config;

const searchClient = SearchClientFactory.create(searchConfig);

const curatedFilters = config.curatedSearch?.filters as
    | CuratedSearchFilters
    | undefined;
const isCuratedSearchPage = !!curatedFilters;

if (typeof window !== "undefined") {
    initRangeFiltersForSSR(window.location.href);
}

if (curatedFilters) {
    setCuratedSearchConfig({
        isActive: true,
        marketplaceEquivalentUrl: config.curatedSearch?.marketplaceUrl ?? null,
        filters: curatedFilters,
    });
    applyCuratedSearchFiltersForSSR(curatedFilters);
}

const isExcerptMode = Boolean(config.excerptMode);
const isMarketplaceGlobal = Boolean(config.isMarketplaceGlobal);
const shouldRedirectFiltersToMarketplace = !isMarketplaceGlobal;
/** Landing excerpts: pagination uses full navigation (?page=) — helper.setPage bypasses the router. */
const useNativePaginationNavigation = shouldRedirectFiltersToMarketplace;
const HITS_PER_PAGE = config.hitsPerPage ?? 48;

const ENERGY_LABELS: Record<string, string> = {
    diesel: "Diesel",
    essence: "Essence",
    hybrid: "Hybride",
    electric: "Électrique",
};

const PREDEFINED_EXTERIOR_COLOR_ORDER = [
    "Blanc",
    "Gris",
    "Noir",
    "Bleu",
    "Vert",
    "Violet",
    "Rouge",
    "Orange",
    "Jaune",
    "Marron",
    "Beige",
    "Argent",
    "Or",
    "Bronze",
];

const PREDEFINED_INTERIOR_COLOR_ORDER = [
    "Noir",
    "Gris",
    "Blanc",
    "Marron",
    "Crème",
    "Rouge",
    "Bleu",
    "Bordeaux",
];

/**
 * Curated SEO search: the widgets keep the look of Vue Vehicle/Index (Marketplace.vue,
 * SortBy.vue, VehicleHits.vue, Pagination.vue) instead of the canonical /marketplace ones.
 */
const isCuratedLayout = Boolean(config.curatedLayout);

/** Tris (sort-options.ts) : les anciens (puissance, année) compris mais masqués des menus. */
const SORT_ITEMS = marketplaceSortItems(searchConfig.indexName, isCuratedLayout);
const visibleSorts = <T extends { value: string }>(options: T[], current: string): T[] =>
    visibleSortOptions(options, SORT_ITEMS, current);

function getSortTriggerLabel(
    currentRefinement: string,
    options: { label: string; value: string }[],
): string {
    // SortBy.vue always reads « Trier ».
    if (isCuratedLayout || currentRefinement === searchConfig.indexName) {
        return "Trier";
    }

    return (
        options.find((item) => item.value === currentRefinement)?.label ??
        "Trier"
    );
}

function createActionBarNativeSortWidget() {
    // Barre de filtres + copies du « Trier » de la barre e2 (titre, panneau : `sort-mirror.blade.php`)
    const selects = Array.from(
        document.querySelectorAll<HTMLSelectElement>(
            ".marketplace-action-bar .hits-sort-by__select-native, [data-sort-mirror] .hits-sort-by__select-native",
        ),
    );
    // Feuille « Trier par » de la barre e3 : branchée ici même sans <select> (bloc d'annonces sans copie du tri)
    const e3Sheet = document.getElementById("mkt-e3-sort-sheet");
    if (selects.length === 0 && !e3Sheet) return null;
    let refineSort: ((value: string) => void) | null = null;
    document.addEventListener(E3_SORT_REFINE_EVENT, (event) => {
        const value = (event as CustomEvent<string>).detail;
        if (typeof value === "string" && refineSort) refineSort(value);
    });

    return connectSortBy((renderState) => {
        const { currentRefinement, refine } = renderState;
        refineSort = refine;
        if (e3Sheet) e3Sheet.dataset.currentSort = currentRefinement;
        const options = visibleSorts(renderState.options, currentRefinement);
        selects.forEach((select) => {
            if (
                select.options.length !== options.length ||
                Array.from(select.options).some((option, i) => option.value !== options[i]?.value)
            ) {
                select.innerHTML = options
                    .map(
                        (item) =>
                            `<option value="${item.value}">${item.label}</option>`,
                    )
                    .join("");
            }
            if (select.value !== currentRefinement) {
                select.value = currentRefinement;
            }
            select.onchange = () => {
                refine(select.value);
            };

            const wrap = select.closest<HTMLElement>(".hits-sort-by__native-wrap");
            // Point jaune de l'icône « Trier » (variante e2) : tri autre que celui par défaut
            wrap?.classList.toggle(
                "is-sorted",
                currentRefinement !== searchConfig.indexName,
            );
            const label = wrap?.querySelector<HTMLElement>(".hits-sort-by__native-label");
            if (label) {
                // Panneau : déjà titré « Trier », la pastille nomme le tri en cours (« Pertinence »)
                label.textContent = select.closest(".sort-mirror--panel")
                    ? (options.find((item) => item.value === currentRefinement)?.label ?? "Pertinence")
                    : getSortTriggerLabel(currentRefinement, options);
            }
        });
    })({ items: SORT_ITEMS });
}

function buildSortWidgets(): ReturnType<typeof createDesktopSortByWidget>[] {
    const widgets: ReturnType<typeof createDesktopSortByWidget>[] = [];

    const nativeSortWidget = createActionBarNativeSortWidget();
    if (nativeSortWidget) {
        widgets.push(nativeSortWidget);
    }

    if (document.getElementById("sort-by")) {
        widgets.push(createDesktopSortByWidget());
    }

    return widgets;
}

function stripWhitespaceNodes(root: Node): void {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const blanks: Node[] = [];
    while (walker.nextNode()) {
        if (!walker.currentNode.textContent?.trim()) blanks.push(walker.currentNode);
    }
    blanks.forEach((node) => node.parentNode?.removeChild(node));
}

function createDesktopSortByWidget() {
    return connectSortBy((renderState, isFirstRender) => {
        const container = document.getElementById("sort-by");
        if (!container) return;

        if (isFirstRender) {
            container.innerHTML = `
                <div class="hits-sort-by">
                    <button class="hits-sort-by__button" type="button" data-attr="listing-sort-open" aria-expanded="false" aria-haspopup="listbox">
                        <span>
                            <svg class="hits-sort-by__button__icon" width="1rem" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M3.33203 5.66797H12.6654H3.33203Z" fill="currentColor"/>
                                <path d="M3.33203 5.66797H12.6654" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                                <path d="M1 1H15H1Z" fill="currentColor"/>
                                <path d="M1 1H15" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                                <path d="M5.66797 10.332H10.3346H5.66797Z" fill="currentColor"/>
                                <path d="M5.66797 10.332H10.3346" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            <span class="hits-sort-by__button__label">Trier</span>
                        </span>
                        <svg class="hits-sort-by__button__arrow" width="1rem" height="1rem" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M1.95312 0.332031L0.953125 1.33203L5 5.37891L9.04688 1.33203L8.04688 0.332031L5 3.37891L1.95312 0.332031Z" fill="currentColor"/>
                        </svg>
                        <span class="hits-sort-by__button__stats"></span>
                    </button>
                    <ul class="hits-sort-by__options" role="listbox"></ul>
                </div>
            `;

            const btn = container.querySelector<HTMLButtonElement>(
                ".hits-sort-by__button",
            );
            const opts = container.querySelector<HTMLUListElement>(
                ".hits-sort-by__options",
            );
            // SortBy.vue: Vue condenses the whitespace between the button's children.
            // and wraps the hidden count in an inline-block ais-Stats.
            if (isCuratedLayout && btn) {
                stripWhitespaceNodes(btn);
                const stats = btn.querySelector(".hits-sort-by__button__stats");
                if (stats) {
                    const wrap = document.createElement("div");
                    wrap.className = "ais-Stats";
                    stats.replaceWith(wrap);
                    wrap.appendChild(stats);
                }
            }
            // SortBy.vue's trigger carries no ARIA state.
            if (isCuratedLayout) {
                btn?.removeAttribute("aria-expanded");
                btn?.removeAttribute("aria-haspopup");
                btn?.removeAttribute("type");
            }
            btn?.addEventListener("click", (e) => {
                e.stopPropagation();
                const isOpen = opts?.classList.toggle(
                    "hits-sort-by__options--show",
                );
                if (!isCuratedLayout) btn.setAttribute("aria-expanded", String(isOpen ?? false));
            });
            document.addEventListener("click", () => {
                opts?.classList.remove("hits-sort-by__options--show");
                if (!isCuratedLayout) btn?.setAttribute("aria-expanded", "false");
            });
        }

        const label = container.querySelector<HTMLElement>(
            ".hits-sort-by__button__label",
        );
        if (label) {
            label.textContent = getSortTriggerLabel(
                renderState.currentRefinement,
                renderState.options,
            );
        }

        const opts = container.querySelector<HTMLUListElement>(
            ".hits-sort-by__options",
        );
        if (!opts) return;
        opts.innerHTML = visibleSorts(renderState.options, renderState.currentRefinement)
            .map((item) => {
                const isActive = item.value === renderState.currentRefinement;
                return `
                <li role="option" aria-selected="${isActive}">
                    <button
                        class="hits-sort-by__option${
                            isActive ? " is-active" : ""
                        }"
                        data-value="${item.value}"
                        data-attr="listing-sort-option"
                        ${isActive ? "disabled" : ""}
                        type="button"
                    >${item.label}</button>
                </li>
            `;
            })
            .join("");
        // SortBy.vue's list has no whitespace between its items.
        if (isCuratedLayout) stripWhitespaceNodes(opts);
        opts.querySelectorAll<HTMLButtonElement>(
            ".hits-sort-by__option:not([disabled])",
        ).forEach((btn) => {
            btn.addEventListener("click", () => {
                renderState.refine(btn.dataset.value ?? "");
                opts.classList.remove("hits-sort-by__options--show");
                container
                    .querySelector<HTMLButtonElement>(".hits-sort-by__button")
                    ?.setAttribute("aria-expanded", "false");
            });
        });
    })({ items: SORT_ITEMS });
}

const sortWidgets = buildSortWidgets();

// ── Landing page pre-refinements ─────────────────────────────
const landingMake =
    config.landingData?.vehicleMake?.designation ??
    config.landingData?.vehicleMake?.label;
const landingModel =
    config.landingData?.vehicleModel?.designation ??
    config.landingData?.vehicleModel?.label;
const landingConcentratedModels =
    config.landingData?.concentratedModelDesignations ?? [];
const landingSpecification =
    config.landingData?.vehicleSpecification?.designation ??
    config.landingData?.vehicleSpecification?.label;

const landingModelRefinements =
    landingConcentratedModels.length > 0
        ? landingConcentratedModels
        : landingModel
        ? [landingModel]
        : [];

if (shouldRedirectFiltersToMarketplace && !isCuratedSearchPage) {
    const landingFilters: CuratedSearchFilters = {};
    if (landingMake) {
        landingFilters.menu = { make: landingMake };
    }

    const landingRefinementList: Record<string, string[]> = {};
    if (landingModelRefinements.length > 0) {
        landingRefinementList.model = [...landingModelRefinements];
    }
    if (landingSpecification) {
        landingRefinementList.specification = [landingSpecification];
    }
    if (Object.keys(landingRefinementList).length > 0) {
        landingFilters.refinementList = landingRefinementList;
    }

    if (landingFilters.menu || landingFilters.refinementList) {
        setCuratedSearchConfig({
            isActive: true,
            marketplaceEquivalentUrl: null,
            filters: landingFilters,
        });
    }
}

// Curated pages: no initialUiState — same as Vue client (app.ts). Routing + routeToState
// merges curatedSearchConfig; passing both initialUiState and routing causes conflicts.
const initialUiState: Record<string, any> = {
    [searchConfig.indexName]: {
        refinementList: {
            ...(landingModelRefinements.length
                ? { model: landingModelRefinements }
                : {}),
            ...(landingSpecification
                ? { specification: [landingSpecification] }
                : {}),
        },
    },
};

if (landingMake) {
    initialUiState[searchConfig.indexName].menu = {
        make: landingMake,
    };
}

// Curated guard state (mirrors Marketplace.vue handleAlgoliaStateChange)
let hasPendingUserFacetRefine = false;
/** Blocks restoreCuratedUiStateIfDrifted until Algolia routing syncs the URL. */
let userFilterGraceUntil = 0;
let isCuratedReady = false;
let curatedRedirectPending = false;

const MARKETPLACE_SSR_FALLBACK_KEY = "marketplace-ssr-search-fallback";
let ssrFallbackTimer: ReturnType<typeof setTimeout> | null = null;

function clearMarketplaceSsrFallbackState(): void {
    sessionStorage.removeItem(MARKETPLACE_SSR_FALLBACK_KEY);
    clearSsrFallbackTimer();
}

function clearSsrFallbackTimer(): void {
    if (ssrFallbackTimer !== null) {
        clearTimeout(ssrFallbackTimer);
        ssrFallbackTimer = null;
    }
}

function scheduleSsrFallbackIfClientHitsMissing(): void {
    if (
        marketplaceHitsSsrRemoved ||
        marketplaceHitsReconciled ||
        sessionStorage.getItem(MARKETPLACE_SSR_FALLBACK_KEY)
    ) {
        return;
    }

    clearSsrFallbackTimer();
    ssrFallbackTimer = setTimeout(() => {
        ssrFallbackTimer = null;
        const mount = document.getElementById("hits-instantsearch");
        if (mount?.querySelector(".ais-Hits")) return;
        if (!document.getElementById("marketplace-hits-ssr")) return;

        sessionStorage.setItem(MARKETPLACE_SSR_FALLBACK_KEY, "1");
        window.location.reload();
    }, 2000);
}

const markUserFilterInteraction = (): void => {
    hasPendingUserFacetRefine = true;
    userFilterGraceUntil = Date.now() + 1000;
    scheduleSsrFallbackIfClientHitsMissing();
};

const isUserFilterGraceActive = (): boolean =>
    Date.now() < userFilterGraceUntil;

/** Mirrors Vue previousSelectedMake — detect brand switches in onStateChange. */
let previousSelectedMakes: string[] = [];

function initPreviousMakeFromUrl(): void {
    if (typeof window === "undefined") return;
    const routeState = parseUrlToRouteState(window.location.href);
    const fromList = routeState.refinementList?.make;
    const fromMenu = routeState.menu?.make;
    previousSelectedMakes = fromList?.length
        ? fromList
        : typeof fromMenu === "string"
        ? [fromMenu]
        : [];
}

initPreviousMakeFromUrl();

const landingPageInitialUiState = ((): Record<string, unknown> | null => {
    if (!useNativePaginationNavigation) {
        return null;
    }

    const instantSearchPage = landingDisplayPageToInstantSearchPage(
        resolveLandingDisplayPageFromUrl(),
    );

    if (instantSearchPage <= 0) {
        return null;
    }

    return {
        [searchConfig.indexName]: { page: instantSearchPage },
    };
})();

const landingExcerptInitialUiState = ((): Record<string, unknown> | null => {
    if (!shouldRedirectFiltersToMarketplace) {
        return null;
    }

    const indexName = searchConfig.indexName;
    const mergedIndexState = {
        ...(initialUiState[indexName] ?? {}),
        ...(landingPageInitialUiState?.[indexName] ?? {}),
    };

    if (Object.keys(mergedIndexState).length === 0) {
        return null;
    }

    return { [indexName]: mergedIndexState };
})();

// ── InstantSearch instance ───────────────────────────────────
const search = instantsearch({
    indexName: searchConfig.indexName,
    searchClient,
    routing: isMarketplaceGlobal
        ? createMarketplaceBladeRouting(searchConfig.indexName)
        : shouldRedirectFiltersToMarketplace
        ? createAlgoliaRouting(searchConfig.indexName)
        : false,
    ...(shouldRedirectFiltersToMarketplace
        ? landingExcerptInitialUiState
            ? { initialUiState: landingExcerptInitialUiState }
            : {}
        : { initialUiState }),
    onStateChange: shouldRedirectFiltersToMarketplace
        ? createCuratedPageStateChangeHandler({
              indexName: searchConfig.indexName,
              isCuratedSearchPage,
              shouldRedirectFiltersToMarketplace: true,
              vehicleIndexParity: isCuratedLayout,
              getHasPendingUserFacetRefine: () => hasPendingUserFacetRefine,
              clearHasPendingUserFacetRefine: () => {
                  hasPendingUserFacetRefine = false;
              },
              getUserFilterGraceActive: isUserFilterGraceActive,
              getIsCuratedReady: () => isCuratedReady,
              isCuratedRedirectPending: () => curatedRedirectPending,
              setCuratedRedirectPending: (value) => {
                  curatedRedirectPending = value;
              },
          })
        : ({ uiState, setUiState }) => {
              const indexName = searchConfig.indexName;
              initInstantSearchBridge(setUiState, uiState, indexName);

              const indexState = uiState[indexName] as
                  | {
                        menu?: { make?: string };
                        refinementList?: Record<string, string[]>;
                    }
                  | undefined;

              const currentSelectedMakes = normalizeMakeSelection(indexState);

              if (
                  applyMakeChangeDependentResets(
                      uiState,
                      indexName,
                      previousSelectedMakes,
                      currentSelectedMakes,
                  )
              ) {
                  syncSpecificationSearchUi();
              }

              setUiState(uiState);
              previousSelectedMakes = currentSelectedMakes;
          },
});

// ── Make facet (multi-select refinementList) ─────────────────
const marketplaceMakeMenu = createMakeMenuWidget();

// ── Widget registration ──────────────────────────────────────
// Dynamic configure (specification + curated filters) on every search.
search.addWidgets([
    createCuratedConfigureWidget(isCuratedLayout ? curatedHitsPerPage : HITS_PER_PAGE) as never,
]);

// Vue teleports Autocomplete (`aa-*`) to `#header-searchbox-mobile` below 960px.
// Blade `/marketplace` uses that chrome at all widths (`html.marketplace-compact-layout`)
// via `createMarketplaceHeaderAutocompleteWidget` (recent searches, brands, suggestions).
function isMarketplaceCompactLayout(): boolean {
    return document.documentElement.classList.contains(
        "marketplace-compact-layout",
    );
}

/** Curated page: the search box was mounted in the header (below 960px at load). */
let curatedSearchMountedInHeader = false;

/** Branché après le démarrage de la recherche (initSearchQueryFilters) ; avant, la recherche part telle quelle. */
let resolveSearchBeforeCommit: (query: string) => Promise<boolean> = () => Promise.resolve(false);

function createMarketplaceSearchBoxWidget() {
    const isMobileHeaderSearch =
        isMarketplaceCompactLayout() ||
        window.matchMedia("(max-width: 959px)").matches;
    const hasHeaderSearch = Boolean(document.getElementById("header-searchbox-mobile"));

    // Curated page: MarketplaceHeader.vue mounts Autocomplete.vue in the hero before its
    // teleport turns on, so the hero options (Algolia panel positioning, « Rechercher »
    // button) are kept at every width, in the header below 960px too. Enter or a
    // suggestion leave for /marketplace, an iconic brand goes through the drift guard like
    // Autocomplete.vue's updateAlgoliaState.
    if (isCuratedLayout) {
        curatedSearchMountedInHeader = isMobileHeaderSearch && hasHeaderSearch;
        return createMarketplaceHeaderAutocompleteWidget(search, searchConfig, {
            container: curatedSearchMountedInHeader ? "#header-searchbox-mobile" : "#searchbox",
            placeholder: "Marque, modèle, finition…",
            redirectToMarketplaceOnSelect: true,
            brandSetsUiState: true,
            headerPanel: false,
            searchButton: true,
        });
    }

    if (isMobileHeaderSearch && hasHeaderSearch) {
        return createMarketplaceHeaderAutocompleteWidget(search, searchConfig, {
            container: "#header-searchbox-mobile",
            // Liste filtrée au téléphone : « Trier » partage la ligne, placeholder court (listing-filtered-state.ts)
            placeholder: searchPlaceholderFor(isListingFilteredOnLoad(), isNarrowViewport()),
            redirectToMarketplaceOnSelect: shouldRedirectFiltersToMarketplace,
            ...(isMarketplaceGlobal ? { resolveBeforeCommit: (query: string) => resolveSearchBeforeCommit(query) } : {}),
        });
    }

    return searchBox({
        container: "#searchbox",
        placeholder: "Marque, modèle, finition…",
        searchAsYouType: false,
        showReset: true,
        showSubmit: true,
        cssClasses: {
            root: "marketplace-searchbox",
            input: "ais-SearchBox-input",
        },
        templates: {
            submit: () => "Rechercher",
        },
    });
}

search.addWidgets([
    createMarketplaceSearchBoxWidget(),

    stats({
        container: "#stats",
        templates: {
            text(data: any) {
                return `${data.nbHits}`;
            },
        },
    }),

    isCuratedLayout
        ? createCuratedCurrentRefinementsWidget({
              container: "#current-refinements",
              bodyTypeLabels,
              gearboxTypeLabels,
          })
        : currentRefinements({
              container: "#current-refinements",
              cssClasses: {
                  root: "current-refinements",
                  list: "current-refinements__list",
                  item: "current-refinements__item",
                  label: "current-refinements__label",
                  category: "current-refinements__category",
                  delete: "current-refinements__delete",
              },
          }),

    // Curated page: Vue markup through createCuratedFacetWidgets() (end of the list).
    // « Réinitialiser » : facettes, mais aussi plages et finition (hors refinements InstantSearch).
    ...(isCuratedLayout ? [] : [createClearAllFiltersWidget("#clear-refinements")].filter((w) => w !== null)),

    // 1. Marque — omitted on landing-spec page (no #menu-make container)
    ...(document.getElementById("menu-make") ||
    document.getElementById("action-bar-make")
        ? [marketplaceMakeMenu]
        : []),

    // 2. Modèle
    ...([
        document.getElementById("refinement-model") ||
        document.getElementById("action-bar-model")
            ? window.__MARKETPLACE_CONFIG__?.initialModelsFacet?.length
                ? [createModelRefinementWidget()]
                : [
                      createMenuStyleRefinementWidget(
                          [
                              "#refinement-model",
                              "#action-bar-model",
                          ].filter((sel) => document.querySelector(sel)),
                          "model",
                          {
                              searchable: true,
                              placeholder: "Rechercher un modèle…",
                              showMore: true,
                              showMoreLimit: 100,
                              sortBy: ["isRefined", "count:desc"],
                              multiple: true,
                              groupByModelFamily: true,
                          },
                      ),
                  ]
            : null,
    ].flat().filter((w) => w !== null) as NonNullable<
        ReturnType<typeof createMenuStyleRefinementWidget>
    >[]),

    // 3. Finition et Motorisation (SimpleSearchBox-style)
    ...([
        createSpecificationSearchWidget("#refinement-specification", search),
    ].filter((w) => w !== null) as NonNullable<
        ReturnType<typeof createSpecificationSearchWidget>
    >[]),

    // 4. Options
    ...([
        createMenuStyleRefinementWidget("#refinement-options", "options", {
            searchable: true,
            placeholder: "Rechercher une option…",
            showMore: true,
            showMoreLimit: 500,
            operator: "and",
            sortBy: ["name:asc"],
            multiple: true,
        }),
    ].filter((w) => w !== null) as NonNullable<
        ReturnType<typeof createMenuStyleRefinementWidget>
    >[]),

    // 5–7. Prix, Kilométrage, Mensualité
    ...([
        document.getElementById("range-price") ||
        document.getElementById("action-bar-price")
            ? createCustomRangeWidget(
                  ["#range-price", "#action-bar-price"],
                  "price",
                  search,
              )
            : null,
        document.getElementById("range-mileage") ||
        document.getElementById("action-bar-mileage")
            ? createCustomRangeWidget(
                  ["#range-mileage", "#action-bar-mileage"],
                  "mileage",
                  search,
              )
            : null,
        createCustomRangeWidget(
            ["#range-monthly-price", "#action-bar-monthly-price"],
            "monthly_price",
            search,
        ),
    ].filter((w) => w !== null) as NonNullable<
        ReturnType<typeof createCustomRangeWidget>
    >[]),

    // 8–9. Couleurs extérieure et intérieure
    ...([
        createColorRefinementWidget(
            "#refinement-exterior-color",
            "reference_exterior_color",
            referenceExteriorColors,
            FALLBACK_EXTERIOR_COLORS,
            PREDEFINED_EXTERIOR_COLOR_ORDER,
        ),
        createColorRefinementWidget(
            "#refinement-interior-color",
            "reference_interior_color",
            referenceInteriorColors,
            {},
            PREDEFINED_INTERIOR_COLOR_ORDER,
            ["Couleur intérieure indéfinie"],
        ),
    ].filter((w) => w !== null) as NonNullable<
        ReturnType<typeof createColorRefinementWidget>
    >[]),

    // 10. Matériau intérieur
    ...([
        createMaterialRefinementWidget(
            "#refinement-interior-material",
            "reference_interior_material",
        ),
    ].filter((w) => w !== null) as NonNullable<
        ReturnType<typeof createMaterialRefinementWidget>
    >[]),

    // 11. Année
    ...([
        createCustomRangeWidget("#range-year", "registration_at", search),
    ].filter((w) => w !== null) as NonNullable<
        ReturnType<typeof createCustomRangeWidget>
    >[]),

    // 12. Carburant (sidebar pills + tablet+ action-bar dropdown)
    ...([
        document.getElementById("refinement-energy") ||
        document.getElementById("action-bar-energy")
            ? createButtonFacetWidget({
                  container: ["#refinement-energy", "#action-bar-energy"],
                  attribute: "energy_type",
                  sortBy: ["name:asc"],
                  transformItems(items) {
                      return items.map((item) => ({
                          ...item,
                          label: ENERGY_LABELS[item.value] ?? item.label,
                          highlighted:
                              ENERGY_LABELS[item.value] ?? item.highlighted,
                      }));
                  },
              })
            : null,
    ].filter((w) => w !== null) as NonNullable<
        ReturnType<typeof createButtonFacetWidget>
    >[]),

    // 12b. Prix en baisse (action-bar toggle)
    ...([createPromotionToggleWidget()].filter(
        (w) => w !== null,
    ) as NonNullable<ReturnType<typeof createPromotionToggleWidget>>[]),

    // 13–14. Poids, CO2
    ...([
        createCustomRangeWidget("#range-weight", "weight", search),
        createCustomRangeWidget("#range-co2", "co2_emission", search),
    ].filter((w) => w !== null) as NonNullable<
        ReturnType<typeof createCustomRangeWidget>
    >[]),

    // 15. Puissance
    ...([createCustomRangeWidget("#range-power", "power", search)].filter(
        (w) => w !== null,
    ) as NonNullable<ReturnType<typeof createCustomRangeWidget>>[]),

    // 16. Places
    ...([
        createButtonFacetWidget({
            container: "#refinement-seats",
            attribute: "seats",
            sortBy: ["name:asc"],
            filterValues: ["4", "5", "7", "9"],
        }),
    ].filter((w) => w !== null) as NonNullable<
        ReturnType<typeof createButtonFacetWidget>
    >[]),

    // 17. Boîte
    ...([
        createButtonFacetWidget({
            container: "#refinement-gearbox",
            attribute: "gearbox",
            sortBy: ["name:asc"],
            transformItems(items) {
                return items.map((item) => ({
                    ...item,
                    label: gearboxTypeLabels[item.value] ?? item.label,
                    highlighted:
                        gearboxTypeLabels[item.value] ?? item.highlighted,
                }));
            },
        }),
    ].filter((w) => w !== null) as NonNullable<
        ReturnType<typeof createButtonFacetWidget>
    >[]),

    // 18. Carrosserie
    ...([
        createButtonFacetWidget({
            container: "#refinement-body-type",
            attribute: "body_type",
            sortBy: ["name:asc"],
            transformItems(items) {
                return items.map((item) => ({
                    ...item,
                    label: bodyTypeLabels[item.value] ?? item.label,
                    highlighted: bodyTypeLabels[item.value] ?? item.highlighted,
                }));
            },
        }),
    ].filter((w) => w !== null) as NonNullable<
        ReturnType<typeof createButtonFacetWidget>
    >[]),

    createReconciledMarketplaceHitsWidget({
        container: "#hits-instantsearch",
        ssrQueryKey: config.ssrQueryKey ?? null,
        cssClasses: {
            root: "marketplace-hits",
            list: "marketplace-hits__list",
            item: "marketplace-hits__item",
        },
        transformItems(items, { results }) {
            // VehicleHits.vue (curated page) has no promo card.
            return markMarketplaceLcpHit(
                isCuratedLayout ? items : injectPromoCards(items, results),
            );
        },
        templates: {
            item(hit: any) {
                if (isPromoCard(hit)) return promoCardTemplate();
                return vehicleCardTemplate(hit);
            },
            empty() {
                if (isCuratedLayout) return curatedNoResultsTemplate();
                return '<div class="marketplace-hits__empty"><p>Aucun véhicule ne correspond à vos critères.</p></div>';
            },
        },
        onReconciled: clearMarketplaceSsrFallbackState,
        onSsrRemoved: clearMarketplaceSsrFallbackState,
    }),

    connectPagination((renderState) => {
        const container = document.getElementById("pagination");
        if (!container) return;

        if (isCuratedLayout) {
            renderCuratedPagination(container, renderState);
            return;
        }

        const {
            currentRefinement,
            nbPages,
            isFirstPage,
            isLastPage,
            refine,
            createURL,
        } = renderState;

        const MILESTONE_PAGES = [10, 20, 30, 50, 100, 1000, 10000];

        const getDisplayedPages = (
            current: number,
            total: number,
        ): (number | "...")[] => {
            const pageSet = new Set<number>();

            for (let i = 1; i <= Math.min(3, total); i++) pageSet.add(i);
            if (current > 1) pageSet.add(current - 1);
            pageSet.add(current);
            if (current < total) pageSet.add(current + 1);
            for (const m of MILESTONE_PAGES) {
                if (m <= total) pageSet.add(m);
            }
            if (total > 0) pageSet.add(total);

            const sorted = Array.from(pageSet).sort((a, b) => a - b);
            const pages: (number | "...")[] = [];
            for (let i = 0; i < sorted.length; i++) {
                if (i > 0 && sorted[i] - sorted[i - 1] > 1) pages.push("...");
                pages.push(sorted[i]);
            }
            return pages;
        };

        const landingDisplayPage = useNativePaginationNavigation
            ? resolveLandingDisplayPageFromUrl()
            : currentRefinement + 1;
        const currentPage = landingDisplayPage;
        const pages = getDisplayedPages(currentPage, nbPages);
        const isFirstPageEffective = useNativePaginationNavigation
            ? landingDisplayPage <= 1
            : isFirstPage;
        const isLastPageEffective = useNativePaginationNavigation
            ? landingDisplayPage >= nbPages
            : isLastPage;

        const pageUrl = (displayPage: number): string => {
            if (useNativePaginationNavigation) {
                return buildLandingPaginationUrl(
                    window.location,
                    displayPage,
                );
            }

            return createURL(displayPage - 1);
        };

        const prevDisabled = isFirstPageEffective
            ? " marketplace-pagination__link--disabled"
            : "";
        const nextDisabled = isLastPageEffective
            ? " marketplace-pagination__link--disabled"
            : "";

        const pageItems = pages
            .map((page) => {
                if (page === "...") {
                    return `<li class="marketplace-pagination__item marketplace-pagination__item--ellipsis">…</li>`;
                }
                const selected =
                    page === currentPage
                        ? " marketplace-pagination__item--selected"
                        : "";
                const url = pageUrl(page);
                return `<li class="marketplace-pagination__item${selected}">
                <a data-attr="listing-page-link" class="marketplace-pagination__link" href="${url}" data-page="${
                    page - 1
                }">${page}</a>
            </li>`;
            })
            .join("");

        container.innerHTML = `
            <nav class="marketplace-pagination">
                <ul class="marketplace-pagination__list">
                    <li class="marketplace-pagination__item">
                        <a data-attr="listing-page-link" class="marketplace-pagination__link${prevDisabled}" href="${
                            isFirstPageEffective ? "#" : pageUrl(currentPage - 1)
                        }" data-page="${
                            currentRefinement - 1
                        }" aria-label="Page précédente">‹</a>
                    </li>
                    ${pageItems}
                    <li class="marketplace-pagination__item">
                        <a data-attr="listing-page-link" class="marketplace-pagination__link${nextDisabled}" href="${
                            isLastPageEffective ? "#" : pageUrl(currentPage + 1)
                        }" data-page="${
                            currentRefinement + 1
                        }" aria-label="Page suivante">›</a>
                    </li>
                </ul>
            </nav>`;

        container
            .querySelectorAll<HTMLAnchorElement>("a[data-page]")
            .forEach((link) => {
                link.addEventListener("click", (e) => {
                    if (
                        link.classList.contains(
                            "marketplace-pagination__link--disabled",
                        )
                    ) {
                        e.preventDefault();
                        return;
                    }

                    if (useNativePaginationNavigation) {
                        // Full navigation: SSR + URL sync (helper.setPage skips routing).
                        return;
                    }

                    const page = parseInt(link.dataset.page ?? "0", 10);
                    if (!isNaN(page)) {
                        e.preventDefault();
                        refine(page);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                    }
                });
            });
    })({}),

    ...sortWidgets,

    ...((isCuratedLayout ? createCuratedFacetWidgets() : []) as never[]),

    ...(document.getElementById("sort-by-mobile")
        ? [
              sortBy({
                  container: "#sort-by-mobile",
                  items: SORT_ITEMS,
                  transformItems: (items) =>
                      items.filter((item) => !SORT_ITEMS.some((sort) => sort.hidden && sort.value === item.value)),
                  cssClasses: {
                      root: "marketplace-sort marketplace-sort--mobile",
                      select: "marketplace-sort__select marketplace-sort__select--mobile",
                  },
              }),
          ]
        : []),
]);

// SSR hits grid is reconciled in createReconciledMarketplaceHitsWidget (kept when the
// first client response matches Blade SSR; removed only on query divergence).
search.on("error", (event) => {
    console.error("[marketplace-search] InstantSearch error", event.error);
    document.dispatchEvent(
        new CustomEvent("show-toast", {
            detail: {
                message:
                    "La recherche est temporairement indisponible. Réessayez dans un instant.",
                type: "error",
            },
        }),
    );
    scheduleSsrFallbackIfClientHitsMissing();
});

// Not an InstantSearch widget (no render/init) — registers on search "render".
createRangeFiltersPillsWidget("#range-filter-pills", search);

registerInstantSearchForRangeFilters(search);

if (shouldRedirectFiltersToMarketplace) {
    const redirectStateMapping = createStateMapping(searchConfig.indexName);

    // Range and finition filters leave a curated page for /marketplace (accepted
    // divergence: Marketplace.vue's drift guard drops them, see accepted-divergences.ts).
    setOnUserFilterInteraction(markUserFilterInteraction);

    search.use(() => ({
        $$type: "ais.custom.marketplaceRedirect",
        onStateChange({ uiState }) {
            if (!isCuratedReady || curatedRedirectPending) {
                return;
            }
            if (!hasPendingUserFacetRefine) {
                return;
            }
            hasPendingUserFacetRefine = false;

            const diff = redirectStateMapping.stateToRoute(uiState);
            if (Object.keys(diff).length === 0) {
                return;
            }

            curatedRedirectPending = true;
            suppressNextRouteWrite();
            window.location.href = uiStateToMarketplaceUrl(
                uiState,
                searchConfig.indexName,
            );
        },
    }));
}

search.start();
// Liste filtrée au téléphone : la ligne du titre se replie dès le choix (état de l'index), sans attendre les
// résultats ; le rendu ci-dessous confirme avec le même compte que « Filtrer »
search.mainIndex.getHelper()?.on("change", ({ state }) => {
    syncListingFilteredState(
        countActiveMarketplaceFilters(search.mainIndex.getHelper() as unknown as AlgoliaSearchHelper | null) +
            countHiddenRefinements(hiddenRefinementsState.value),
        state.query,
    );
});
scheduleSsrFallbackIfClientHitsMissing();
// Recherche d'une seule marque (ou d'un seul modèle) : les filtres se posent comme dans « Filtrer »
if (isMarketplaceGlobal) {
    resolveSearchBeforeCommit = initSearchQueryFilters(
        search,
        searchConfig.indexName,
        search.mainIndex.getHelper(),
        (uiState) => uiStateToMarketplaceUrl(uiState as Parameters<typeof uiStateToMarketplaceUrl>[0], searchConfig.indexName, { resetPage: true }),
    );
}

initActionBarFacetDropdowns();
// Helper de l'index (celui que les widgets modifient) : état des filtres en direct pour les pastilles
const marketplaceIndexHelper = () =>
    search.mainIndex.getHelper() as unknown as FacetClearHelper | null;
initActionBarFacetClearButtons(marketplaceIndexHelper);
// Barre mobile e3 (pastilles de valeurs, feuille « Trier par ») : sans effet ailleurs
initMarketplaceBarE3(search, {
    energyLabels: ENERGY_LABELS,
    makeDependentFacets: MAKE_DEPENDENT_REFINEMENT_FACETS,
    onUserFilterInteraction: markUserFilterInteraction,
    leaveForMarketplaceToggling: shouldRedirectFiltersToMarketplace
        ? (attribute, value, pageMakes) => {
              const uiState = search.getUiState() as Record<
                  string,
                  { menu?: Record<string, string>; refinementList?: Record<string, string[]> }
              >;
              const indexState = (uiState[searchConfig.indexName] ??= {});
              const { omitFacets, omitRefinementList } = applyListingChipToUiState(indexState, attribute, value, pageMakes);
              curatedRedirectPending = true;
              suppressNextRouteWrite();
              window.location.href = uiStateToMarketplaceUrl(
                  uiState as Parameters<typeof uiStateToMarketplaceUrl>[0],
                  searchConfig.indexName,
                  { resetPage: true, omitFacets, omitRefinementList },
              );
          }
        : undefined,
});
initMobileScrollChrome();
requestAnimationFrame(() => {
    updateMarketplaceFilterBarStickyTop();
});

if (shouldRedirectFiltersToMarketplace) {
    registerCuratedPathname(window.location.pathname);

    // Curated page: Marketplace.vue flags a user refine only from the facet lists
    // (onFacetRefine / handleMakeRefine / handleModelRefine). Ranges, the finition search,
    // chips, resets and sorts go through its drift guard instead.
    const isCuratedFacetRefineTarget = (target: Element): boolean =>
        (target.classList.contains("RefinementList__checkbox") &&
            !target.classList.contains("RefinementList__clear")) ||
        target.classList.contains("facette-button__input") ||
        target.classList.contains("material-refinement-list__checkbox") ||
        target.classList.contains("select-all-button") ||
        !!target.closest(".color-refinement-list__item");

    const isFilterInteractionTarget = (target: Element): boolean =>
        isCuratedLayout
            ? isCuratedFacetRefineTarget(target)
            : !!target.closest(
            [
                ".marketplace-sidebar",
                ".marketplace-excerpt__filter-root",
                ".marketplace-excerpt__action-bar",
                ".marketplace-global-chrome__action-bar",
                ".marketplace-header__filters",
                "#header-action-bar-mobile",
                "[data-facet-dropdown-panel]",
            ].join(", "),
        ) ||
        target.classList.contains("RefinementList__checkbox") ||
        target.classList.contains("facette-button__input") ||
        target.classList.contains("material-refinement-list__checkbox") ||
        !!target.closest(".color-refinement-list__item");

    document
        .querySelectorAll(".marketplace-page, .marketplace-excerpt")
        .forEach((root) => {
            root.addEventListener(
                "change",
                (event) => {
                    const target = event.target;
                    if (
                        target instanceof Element &&
                        isFilterInteractionTarget(target)
                    ) {
                        markUserFilterInteraction();
                    }
                },
                true,
            );
            root.addEventListener(
                "click",
                (event) => {
                    const target = event.target;
                    if (
                        target instanceof Element &&
                        isFilterInteractionTarget(target)
                    ) {
                        markUserFilterInteraction();
                    }
                },
                true,
            );
        });

    // Marketplace.vue's ais-clear-refinements refines without flagging a user refine.
    if (!isCuratedLayout) {
        document
            .getElementById("clear-refinements")
            ?.addEventListener("click", markUserFilterInteraction, true);
    }

    window.setTimeout(() => {
        isCuratedReady = true;
    }, 300);
}


// ── Mobile sidebar toggles ──────────────────────────────────
const sidebar = document.getElementById("sidebar");
const filterBackdrop = document.getElementById("marketplace-filter-backdrop");
const filterToggle = document.getElementById("filter-toggle");
const sidebarClose = document.getElementById("sidebar-close");
const sidebarValidateBtn = document.getElementById("sidebar-validate-btn");
const mobileSortToggle = document.getElementById("mobile-sort-toggle");
const mobileSortPanel = document.getElementById("mobile-sort-panel");
const mobileSortClose = document.getElementById("mobile-sort-close");

function syncFilterBackdrop(isOpen: boolean): void {
    if (!filterBackdrop) return;
    filterBackdrop.setAttribute("aria-hidden", isOpen ? "false" : "true");
    filterBackdrop.tabIndex = isOpen ? 0 : -1;
}

function toggleSidebar() {
    sidebar?.classList.toggle("open");
    const isOpen = sidebar?.classList.contains("open") ?? false;
    document.body.classList.toggle("app-filter-open", isOpen);
    syncFilterBackdrop(isOpen);
    window.scrollTo({ top: 0, behavior: "smooth" });
}

function closeSidebar() {
    if (!sidebar?.classList.contains("open")) return;
    sidebar.classList.remove("open");
    document.body.classList.remove("app-filter-open");
    syncFilterBackdrop(false);
}

filterBackdrop?.addEventListener("click", closeSidebar);

filterToggle?.addEventListener("click", toggleSidebar);
sidebarClose?.addEventListener("click", closeSidebar);
sidebarValidateBtn?.addEventListener("click", closeSidebar);

document
    .querySelectorAll<HTMLElement>('[data-action="close-filter"]')
    .forEach((el) => el.addEventListener("click", closeSidebar));

document
    .querySelectorAll<HTMLElement>('[data-action="toggle-filter"]')
    .forEach((el) => el.addEventListener("click", toggleSidebar));

// Pastilles et « Filtrer » branchés : rejoue l'appui fait avant le chargement du JS (sur une landing, une
// pastille part directement sur la marketplace filtrée, sans attendre que la page soit prête)
replayPendingActionBarTap();

mobileSortToggle?.addEventListener("click", () => {
    mobileSortPanel?.classList.toggle("open");
    document.body.classList.toggle("app-filter-open");
});
mobileSortClose?.addEventListener("click", () => {
    mobileSortPanel?.classList.remove("open");
    document.body.classList.remove("app-filter-open");
});

// ── Sidebar: scroll to next facet after make/model (Vue Marketplace.vue mobile) ──
type PendingSidebarScroll = {
    widget: string;
    block: "start" | "center";
    openPanel?: boolean;
};

let pendingSidebarScroll: PendingSidebarScroll | null = null;
let pageScrollYBeforeFacetInteraction: number | null = null;

function isMarketplaceFilterPanelOpen(): boolean {
    return sidebar?.classList.contains("open") ?? false;
}

function getSidebarScrollContainer(): HTMLElement | null {
    if (!sidebar?.classList.contains("open")) return null;
    return sidebar;
}

function restorePageScrollAfterSidebarInteraction(): void {
    if (pageScrollYBeforeFacetInteraction === null) return;
    const pageScrollY = pageScrollYBeforeFacetInteraction;
    pageScrollYBeforeFacetInteraction = null;
    const restore = (): void => {
        if (window.scrollY !== pageScrollY) {
            window.scrollTo(0, pageScrollY);
        }
    };
    requestAnimationFrame(restore);
    window.setTimeout(restore, 0);
    window.setTimeout(restore, 50);
    window.setTimeout(restore, 150);
}

function scrollSidebarTo(
    container: HTMLElement,
    el: HTMLElement,
    block: "start" | "center" = "start",
): void {
    const pageScrollY = window.scrollY;
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const stickyHeader = container.querySelector<HTMLElement>(
        ".marketplace-sidebar__header",
    );
    const headerOffset = stickyHeader?.getBoundingClientRect().height ?? 0;
    const targetOffset = Math.max(
        0,
        block === "center"
            ? elRect.top -
                  containerRect.top +
                  container.scrollTop -
                  container.clientHeight / 2 +
                  el.clientHeight / 2
            : elRect.top -
                  containerRect.top +
                  container.scrollTop -
                  headerOffset,
    );
    // Never scroll upward when advancing through make → model → specification.
    const offset = Math.max(container.scrollTop, targetOffset);
    container.scrollTo({ top: offset, behavior: "smooth" });
    const restorePageScroll = (): void => {
        if (window.scrollY !== pageScrollY) {
            window.scrollTo(0, pageScrollY);
        }
    };
    requestAnimationFrame(restorePageScroll);
    window.setTimeout(restorePageScroll, 0);
    window.setTimeout(restorePageScroll, 50);
    window.setTimeout(restorePageScroll, 150);
}

function openWidgetPanel(widget: string): void {
    const panel = sidebar?.querySelector(`[data-widget="${widget}"]`);
    if (!panel) return;
    const btn = panel.querySelector<HTMLButtonElement>(".widget-header");
    if (!btn || btn.getAttribute("aria-expanded") === "true") return;
    const body = panel.querySelector(".widget-panel__body") as HTMLElement;
    const title = btn.querySelector(".widget-title");
    const minusIcon = btn.querySelector(
        ".widget-header__icon--minus",
    ) as HTMLElement;
    const plusIcon = btn.querySelector(
        ".widget-header__icon--plus",
    ) as HTMLElement;
    btn.setAttribute("aria-expanded", "true");
    body?.removeAttribute("hidden");
    title?.classList.add("isOpen");
    if (minusIcon) minusIcon.hidden = false;
    if (plusIcon) plusIcon.hidden = true;
}

function performPendingSidebarScroll(): void {
    if (!pendingSidebarScroll || !isMarketplaceFilterPanelOpen()) return;

    const target = pendingSidebarScroll;
    pendingSidebarScroll = null;

    // Wait for InstantSearch widgets to finish updating the sidebar DOM.
    requestAnimationFrame(() => {
        const container = getSidebarScrollContainer();
        if (!container) return;

        if (target.openPanel) {
            openWidgetPanel(target.widget);
        }

        const el = container.querySelector<HTMLElement>(
            `[data-widget="${target.widget}"]`,
        );
        if (el) {
            scrollSidebarTo(container, el, target.block);
        }
        restorePageScrollAfterSidebarInteraction();
    });
}

function queueSidebarScroll(target: PendingSidebarScroll): void {
    if (!isMarketplaceFilterPanelOpen()) return;
    pendingSidebarScroll = target;
}

sidebar?.addEventListener(
    "pointerdown",
    (event) => {
        if (!isMarketplaceFilterPanelOpen()) return;
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (
            target.closest(
                "input.RefinementList__checkbox, label.RefinementList",
            )
        ) {
            pageScrollYBeforeFacetInteraction = window.scrollY;
        }
    },
    true,
);

sidebar?.addEventListener(
    "change",
    (event) => {
        if (!isMarketplaceFilterPanelOpen()) return;
        const target = event.target;
        if (
            !(target instanceof HTMLInputElement) ||
            !target.classList.contains("RefinementList__checkbox")
        ) {
            return;
        }

        if (target.closest("#menu-make") && target.checked) {
            queueSidebarScroll({
                widget: "model",
                block: "start",
                openPanel: true,
            });
            return;
        }

        if (target.closest("#refinement-model") && target.checked) {
            // Mobile : « Finition et Motorisation » est rangée sous « Plus de critères » ; on
            // passe à la facette suivante de la liste (Mensualité) sans ouvrir le groupe.
            const specification = sidebar?.querySelector('[data-widget="specification"]') ?? null;
            queueSidebarScroll(
                isFacetHiddenInMoreGroup(specification)
                    ? { widget: "monthly_price", block: "start" }
                    : { widget: "specification", block: "start", openPanel: true },
            );
        }
    },
    true,
);

// ── Widget panel accordion toggles ──────────────────────────
document.querySelectorAll(".widget-header").forEach((btn) => {
    btn.addEventListener("click", () => {
        const panel = btn.closest(".widget-panel");
        if (!panel) return;
        const body = panel.querySelector(".widget-panel__body") as HTMLElement;
        const title = btn.querySelector(".widget-title");
        const minusIcon = btn.querySelector(
            ".widget-header__icon--minus",
        ) as HTMLElement;
        const plusIcon = btn.querySelector(
            ".widget-header__icon--plus",
        ) as HTMLElement;
        const isOpen = btn.getAttribute("aria-expanded") === "true";

        btn.setAttribute("aria-expanded", String(!isOpen));

        if (isOpen) {
            body?.setAttribute("hidden", "");
            title?.classList.remove("isOpen");
            if (minusIcon) minusIcon.hidden = true;
            if (plusIcon) plusIcon.hidden = false;
        } else {
            body?.removeAttribute("hidden");
            title?.classList.add("isOpen");
            if (minusIcon) minusIcon.hidden = false;
            if (plusIcon) plusIcon.hidden = true;
        }
    });
});

initSidebarMoreFacets(sidebar ?? document);

if (isCuratedLayout) {
    initCuratedReverseColor();
    initCuratedActionBarTeleport();
    initCuratedScrollRestoration(search);
    initCuratedSearchTeleport(curatedSearchMountedInHeader);
}

// Curated page: WidgetPanel.vue gets `force-open = innerWidth > 960` once, at mount. At
// 960px and below every panel but « Marque » (no force-open) starts closed.
if (isCuratedLayout && window.innerWidth <= 960) {
    sidebar?.querySelectorAll<HTMLElement>(".widget-panel").forEach((panel) => {
        if (panel.dataset.widget === "make") return;
        // No synthetic click: it would count as a visitor engagement (GTM loader).
        const btn = panel.querySelector<HTMLButtonElement>(".widget-header");
        if (btn?.getAttribute("aria-expanded") !== "true") return;
        btn.setAttribute("aria-expanded", "false");
        panel.querySelector(".widget-panel__body")?.setAttribute("hidden", "");
        btn.querySelector(".widget-title")?.classList.remove("isOpen");
        const minus = btn.querySelector<HTMLElement>(".widget-header__icon--minus");
        const plus = btn.querySelector<HTMLElement>(".widget-header__icon--plus");
        if (minus) minus.hidden = true;
        if (plus) plus.hidden = false;
    });
}

// ── Dynamic stats update ─────────────────────────────────────
const totalHitsEl = document.getElementById("total-hits");
const marketplaceGlobalHeadingCount = document.getElementById(
    "marketplace-global-heading-count",
);
const hitsStatsCount = document.getElementById("hits-stats-count");

/** Bouton du panneau Filtrer : « Voir les N véhicules », « disponibles » affiché sur ordinateur. */
function updateSidebarValidateLabel(button: HTMLElement, nbHits: number): void {
    const formatted = formatNumber(nbHits);
    const label = nbHits === 1 ? `Voir le ${formatted} véhicule` : `Voir les ${formatted} véhicules`;
    const suffix = nbHits === 1 ? " disponible" : " disponibles";
    setResultsCount(button, nbHits);
    const labelEl = button.querySelector("#sidebar-validate-label");
    const suffixEl = button.querySelector("#sidebar-validate-suffix");
    if (labelEl && suffixEl) {
        labelEl.textContent = label;
        suffixEl.textContent = suffix;
    } else {
        button.textContent = label + suffix;
    }
}

// « Effacer » du pied du panneau (mobile) : même action que « Réinitialiser » en tête de panneau.
const sidebarValidateClear = document.getElementById("sidebar-validate-clear");
const sidebarValidate = document.getElementById("sidebar-validate");
let clearedFromKeyboard = false;

/**
 * Affiche ou retire « Effacer ». La classe pilote la grille du pied ; la lecture d'offsetWidth
 * force la mise en page tout de suite (Safari iOS repeignait le pied collant en retard). Si
 * « Effacer » avait le focus, il passe au bouton de validation au lieu de se perdre.
 */
function syncSidebarValidateClear(visible: boolean): void {
    if (!sidebarValidateClear || sidebarValidateClear.hidden === !visible) return;
    const hadFocus = document.activeElement === sidebarValidateClear;
    sidebarValidateClear.hidden = !visible;
    sidebarValidate?.classList.toggle("marketplace-sidebar__validate--with-clear", visible);
    void sidebarValidate?.offsetWidth;
    // Au clavier seulement : au toucher, le focus donnerait au bouton sa couleur de survol.
    if (hadFocus && !visible && clearedFromKeyboard) sidebarValidateBtn?.focus({ preventScroll: true });
    clearedFromKeyboard = false;
}
sidebarValidateClear?.addEventListener("click", (event) => {
    clearedFromKeyboard = event.detail === 0;
    markUserFilterInteraction();
    clearAllMarketplaceFilters();
});
const statsMobile = document.getElementById("stats-mobile");

search.on("render", () => {
    if (isCuratedLayout) {
        applyCuratedDisplayLimit();
        const nbHits = search.helper?.lastResults?.nbHits;
        document
            .getElementById("hits")
            ?.classList.toggle("no-results", nbHits === 0);
    }

    if (pendingSidebarScroll) {
        performPendingSidebarScroll();
    }

    const nbHits = search.helper?.lastResults?.nbHits;
    if (nbHits !== undefined) {
        const formatted = formatNumber(nbHits);
        const label = `${formatted} véhicule${nbHits !== 1 ? "s" : ""}`;
        if (totalHitsEl) totalHitsEl.textContent = formatted;
        if (marketplaceGlobalHeadingCount) {
            marketplaceGlobalHeadingCount.textContent = formatted;
        }
        if (sidebarValidateBtn) {
            updateSidebarValidateLabel(sidebarValidateBtn, nbHits);
        }
        if (hitsStatsCount) {
            hitsStatsCount.textContent =
                "countOnly" in hitsStatsCount.dataset ? formatted : label;
        }
        if (isCuratedLayout) {
            // SortBy.vue: hidden « (N) » count of the desktop trigger.
            document
                .querySelectorAll("#sort-by .hits-sort-by__button__stats")
                .forEach((el) => {
                    el.textContent = ` (${formatted}) `;
                });
        }
        if (isCuratedLayout) {
            const validateCount = document.getElementById("sidebar-validate-count");
            const validateUnit = document.getElementById("sidebar-validate-unit");
            if (validateCount) validateCount.textContent = formatted;
            const curatedValidate = document.querySelector<HTMLElement>('[data-testid="sidebar-validate"] [data-action="close-filter"]');
            if (curatedValidate) setResultsCount(curatedValidate, nbHits);
            if (validateUnit) {
                validateUnit.textContent = nbHits === 1 ? "véhicule" : "véhicules";
            }
        }
        if (statsMobile) statsMobile.textContent = `(${formatted})`;
        const actionBarStatsCount = document.getElementById(
            "action-bar-stats-count",
        );
        if (actionBarStatsCount) actionBarStatsCount.textContent = label;
    }

    const activeFilterCount = countActiveMarketplaceFilters(
        search.helper ?? null,
    );
    updateFilterToggleLabels(activeFilterCount);
    // Filtres sans pastille ni compteur (pays fournisseur) : la liste reste filtrée
    const listingFilterCount = activeFilterCount + countHiddenRefinements(hiddenRefinementsState.value);
    const listingState = search.mainIndex.getHelper()?.state;
    syncListingFilteredState(listingFilterCount, listingState?.query);
    // Contexte des clics PostHog (body) : liste filtrée et page affichée
    syncListingTrackingContext(
        isListingFiltered(listingFilterCount, listingState?.query),
        (listingState?.page ?? 0) + 1,
    );
    syncSidebarValidateClear(activeFilterCount > 0);
    updateMoreFacetsActiveCount(countActiveMoreFacets(search.mainIndex.getHelper()?.state));
    updateActionBarFacetDropdownLabels(marketplaceIndexHelper()?.state);
});

// ── Save search modal (signed-in users) ──────────────────────
initMarketplaceSaveSearchModal({
    marketplaceEquivalentUrl: config.curatedSearch?.marketplaceUrl ?? null,
});

// ── Vehicle card carousels (idle-scheduled: keeps the render task short) ──
search.on("render", () => scheduleVehicleCardCarouselInit());
